import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Package, MapPin, Calendar, Thermometer, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Device {
  device_id: string;
  device_nickname: string;
}

interface CreateShipmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  devices: Device[];
  onSuccess: () => void;
}

function CreateShipmentModal({ isOpen, onClose, devices, onSuccess }: CreateShipmentModalProps) {
  const [formData, setFormData] = useState({
    device_id: '',
    name: '',
    description: '',
    origin: '',
    destination: '',
    expected_days: 7,
    temperature_min: '',
    temperature_max: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.device_id || !formData.name || !formData.origin || !formData.destination || !formData.expected_days) {
        throw new Error('Please fill in all required fields');
      }

      const { error: rpcError } = await supabase.rpc('create_shipment', {
        p_device_id: formData.device_id,
        p_name: formData.name,
        p_description: formData.description || null,
        p_origin: formData.origin,
        p_destination: formData.destination,
        p_expected_days: formData.expected_days,
        p_temperature_min: formData.temperature_min ? parseFloat(formData.temperature_min) : null,
        p_temperature_max: formData.temperature_max ? parseFloat(formData.temperature_max) : null,
      });

      if (rpcError) throw rpcError;

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        resetForm();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to create shipment');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      device_id: '',
      name: '',
      description: '',
      origin: '',
      destination: '',
      expected_days: 7,
      temperature_min: '',
      temperature_max: '',
    });
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-traqpack-yellow rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-gray-900" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">New Shipment</h2>
                <p className="text-sm text-gray-500">Create an AI-monitored shipment</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Success State */}
        {success ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Shipment Created!</h3>
            <p className="text-gray-500">Your shipment is now being monitored by our AI agent.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Error Message */}
            {error && (
              <div className="flex items-start space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Device Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tracking Device *
              </label>
              <select
                value={formData.device_id}
                onChange={(e) => setFormData({ ...formData, device_id: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-traqpack-yellow focus:ring-0 transition-colors"
                required
              >
                <option value="">Select a device</option>
                {devices.map((device) => (
                  <option key={device.device_id} value={device.device_id}>
                    {device.device_nickname} ({device.device_id})
                  </option>
                ))}
              </select>
            </div>

            {/* Shipment Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shipment Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Cosmetics to New York"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-traqpack-yellow focus:ring-0 transition-colors"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the shipment contents or special requirements..."
                rows={2}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-traqpack-yellow focus:ring-0 transition-colors resize-none"
              />
            </div>

            {/* Origin & Destination */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Origin *
                </label>
                <input
                  type="text"
                  value={formData.origin}
                  onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
                  placeholder="e.g., Los Angeles, CA"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-traqpack-yellow focus:ring-0 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Destination *
                </label>
                <input
                  type="text"
                  value={formData.destination}
                  onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                  placeholder="e.g., New York, NY"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-traqpack-yellow focus:ring-0 transition-colors"
                  required
                />
              </div>
            </div>

            {/* Expected Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Expected Duration (days) *
              </label>
              <input
                type="number"
                value={formData.expected_days}
                onChange={(e) => setFormData({ ...formData, expected_days: parseInt(e.target.value) || 1 })}
                min="1"
                max="365"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-traqpack-yellow focus:ring-0 transition-colors"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Our AI will alert you if the shipment is delayed
              </p>
            </div>

            {/* Temperature Thresholds */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Thermometer className="w-4 h-4 inline mr-1" />
                Temperature Thresholds (optional)
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <input
                    type="number"
                    value={formData.temperature_min}
                    onChange={(e) => setFormData({ ...formData, temperature_min: e.target.value })}
                    placeholder="Min °C"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-traqpack-yellow focus:ring-0 transition-colors"
                  />
                </div>
                <div>
                  <input
                    type="number"
                    value={formData.temperature_max}
                    onChange={(e) => setFormData({ ...formData, temperature_max: e.target.value })}
                    placeholder="Max °C"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-traqpack-yellow focus:ring-0 transition-colors"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                AI will flag temperature deviations during transit
              </p>
            </div>

            {/* AI Info Box */}
            <div className="bg-traqpack-yellow/10 border border-traqpack-yellow rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-1">AI-Powered Monitoring</h4>
              <p className="text-sm text-gray-600">
                Once created, our AI agent will analyze every GPS update, checking if the route makes sense,
                monitoring temperature, and alerting you to any anomalies.
              </p>
            </div>

            {/* Actions */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-3 bg-traqpack-yellow text-gray-900 hover:bg-yellow-400 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 mr-2"></div>
                    Creating...
                  </span>
                ) : (
                  'Start Shipment'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}

export default CreateShipmentModal;
